import React, { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail, API } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Download, Mail, Receipt, Link, Copy, Upload, Trash2, Printer, FileText, StickyNote, ArrowUpDown, Search, Layers, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ChatPanel from "@/components/ui/ChatPanel";
import { useAuth } from "@/context/AuthContext";
import PodGallery from "@/components/ui/PodGallery";
import AddressAutocomplete from "@/components/ui/AddressAutocomplete";
import StopAddressField from "@/components/ui/StopAddressField";
import SendCnPodModal from "@/components/SendCnPodModal";
import { formatDate, formatDateTime } from "@/lib/utils";
import { DatePicker } from "@/components/ui/date-picker";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import ItemMultiSelect, { parseItemRows, deriveItemFields } from "@/components/ui/ItemMultiSelect";
import TollMultiSelect, { tollTotal, detectTollSelection } from "@/components/ui/TollMultiSelect";

function calcWaitCharge(checkin, checkout) {
  if (!checkin || !checkout) return null;
  const diffMs = new Date(checkout) - new Date(checkin);
  if (diffMs <= 0) return null;
  const total = diffMs / 60000;
  const billable = Math.max(0, total - 30);
  return {
    total: total.toFixed(1),
    billable: billable.toFixed(1),
    charge: (billable * 0.75).toFixed(2),
  };
}

function toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Pricing inputs store 0 as their unset default, but showing a literal "0"
// blocks free typing (new digits land before/after it instead of replacing
// it). Treat 0/null/undefined as blank so the field starts empty.
function blankIfZero(v) {
  return v === 0 || v == null ? "" : v;
}

// Freight type options. "Other (Specify)" reveals a free-text field.
const FREIGHT_TYPES = ["LCL", "Airfreight", "General", "FCL", "Interstate"];
const FREIGHT_OTHER = "Other (Specify)";

// Service type — express bills at double the customer base rate (see
// _effective_base_rate() in backend/server.py). Keep in sync with CreateJob.jsx.
const SERVICE_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "express",  label: "Express (same-day)" },
];

// Accessorial service charges billed to the customer (sell side, AUD) when the
// option is "Yes" — used for the live "Total (incl. GST)" readout below.
// Keep in sync with ACCESSORIAL_CHARGES in backend/pdfgen.py and CreateJob.jsx.
const ACCESSORIAL_CHARGES = [
  ["inbound_connect", "Inbound Connect", 35],
  ["tail_lift",       "Tail Lift",       100],
  ["hand_unload",     "Hand Unload",     55],
  ["dangerous_goods", "Dangerous Goods", 55],
];

// Shipment shape — "single" (one sender, one receiver) is the default and
// covers the vast majority of jobs; the multi_* values are rare and mean
// extra pickup/dropoff-type legs carry their own sender/receiver signature.
const SHIPMENT_MODES = [
  { value: "single",        label: "Single pickup & single delivery" },
  { value: "multi_pickup",  label: "Multiple pickups → one delivery" },
  { value: "multi_dropoff", label: "One pickup → multiple deliveries" },
  { value: "multi_both",    label: "Multiple pickups & multiple deliveries" },
];

// ETA is a delivery DATE + a 2-hour WINDOW rather than an exact time — a
// dropdown is far easier to tap on mobile than a combined date+time picker
// (which reopens the calendar every time). Keep in sync with the copies in
// DriverRuns.jsx and ClientJobs.jsx.
const ETA_WINDOWS = [
  { value: "06:00-08:00", label: "6:00 AM – 8:00 AM" },
  { value: "08:00-10:00", label: "8:00 AM – 10:00 AM" },
  { value: "10:00-12:00", label: "10:00 AM – 12:00 PM" },
  { value: "12:00-14:00", label: "12:00 PM – 2:00 PM" },
  { value: "14:00-16:00", label: "2:00 PM – 4:00 PM" },
  { value: "16:00-18:00", label: "4:00 PM – 6:00 PM" },
  { value: "18:00-20:00", label: "6:00 PM – 8:00 PM" },
];

const STATUS_META = {
  needs_approval: { color: "#f59e0b", label: "Needs Approval" },
  assigned:       { color: "#19e6c8", label: "Assigned" },
  pending:        { color: "#19e6c8", label: "Assigned" }, // backward compat
  onboard:        { color: "#a78bfa", label: "Onboard" },
  done:           { color: "#22c55e", label: "Done" },
  futile:         { color: "#ef4444", label: "Futile" },
};

export default function JobStatus({ historyMode = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [date, setDate] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [active, setActive] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [billFilter, setBillFilter] = useState("all"); // all | merged | unbilled
  const isAdmin = user?.role === "admin";
  // Allocator: full operational access (assign drivers, open/edit jobs) but
  // never the billing-only bits, which stay gated on `isAdmin` specifically
  // (bulk merge-to-invoice, bill filter tabs, Pricing panel, Push Estimate).
  const canOperate = isAdmin || user?.role === "allocator";

  const load = async () => {
    try {
      const { data } = await api.get("/jobs");
      setJobs(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const loadDrivers = async () => {
    if (!["admin", "allocator"].includes(user?.role)) return;
    try {
      const { data } = await api.get("/drivers");
      setDrivers(data);
    } catch {}
  };

  const loadCustomers = async () => {
    if (!["admin", "allocator"].includes(user?.role)) return;
    try {
      const { data } = await api.get("/customers");
      setCustomers(data);
    } catch {}
  };

  useEffect(() => {
    load();
    loadDrivers();
    loadCustomers();
  }, []);

  // Clear the multi-select whenever the visible list changes, so a job that's been
  // filtered out of view can never be silently included in a bulk delete.
  useEffect(() => { setSelected(new Set()); }, [search, date, dateFrom, dateTo, historyMode, billFilter]);

  const filtered = jobs
    .filter((j) => {
      if (historyMode) {
        if (dateFrom && (j.job_date || "") < dateFrom) return false;
        if (dateTo && (j.job_date || "") > dateTo) return false;
      } else if (date && j.job_date !== date) {
        return false;
      }
      const statusOk = historyMode
        ? (j.status === "done" || j.status === "futile")
        : ["assigned", "pending", "onboard"].includes(j.status);
      if (!statusOk) return false;
      // Bill filter: Merged = on a merged invoice; Unbilled = no invoice yet or
      // still draft/not-created (i.e. not issued/sent/paid).
      const ref = j.invoice_ref;
      if (billFilter === "merged" && !(ref && ref.merged)) return false;
      if (billFilter === "unbilled" && ref && ref.invoice_status !== "draft") return false;
      return true;
    })
    .filter((j) => {
      if (!search.trim()) return true;
      const q = search.trim().toLowerCase();
      const driver = drivers.find((d) => d.id === j.assigned_driver_id);
      const customer = customers.find((c) => c.id === j.client_user_id);
      const driverName = (driver?.name || "").toLowerCase();
      const customerName = ((customer?.business_name || customer?.name) || "").toLowerCase();
      return (
        (j.job_ref || "").toLowerCase().includes(q) ||
        (j.carrier_ref || "").toLowerCase().includes(q) ||
        driverName.includes(q) ||
        customerName.includes(q)
      );
    })
    .sort((a, b) => {
      const ta = new Date(a.created_at).getTime();
      const tb = new Date(b.created_at).getTime();
      return sortDir === "desc" ? tb - ta : ta - tb;
    });

  const allSelected = filtered.length > 0 && filtered.every((j) => selected.has(j.id));
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  const toggleSelectAll = () =>
    setSelected(allSelected ? new Set() : new Set(filtered.map((j) => j.id)));
  const exitSelect = () => { setSelectMode(false); setSelected(new Set()); };

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected job${selected.size > 1 ? "s" : ""}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const ids = [...selected];
      const results = await Promise.allSettled(ids.map((id) => api.delete(`/jobs/${id}`)));
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) toast.success(`Deleted ${ids.length} job${ids.length > 1 ? "s" : ""}`);
      else toast.error(`Deleted ${ids.length - failed} of ${ids.length}; ${failed} failed`);
      setSelected(new Set());
      setSelectMode(false);
      await load();
    } finally {
      setBulkBusy(false);
    }
  };

  // "Bill together" — merge the selected jobs' invoices into one consolidated
  // invoice (single customer). Jobs stay separate; only their bills are merged.
  const selectedJobs = jobs.filter((j) => selected.has(j.id));
  const selClientIds = new Set(selectedJobs.map((j) => j.client_user_id || "_none"));
  const billClientId = selClientIds.size === 1 ? [...selClientIds][0] : null;
  const canBillTogether = selectedJobs.length >= 2 && billClientId && billClientId !== "_none";
  const billTogether = async () => {
    if (!canBillTogether) return;
    const ids = selectedJobs.map((j) => j.id);
    if (!window.confirm(`Bill ${ids.length} jobs together as one invoice for this customer? You'll be taken to the invoice to review, edit and send.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/invoices/merge-jobs", { job_ids: ids });
      toast.success(`Merged into ${data.invoice_number}`);
      setSelected(new Set());
      setSelectMode(false);
      navigate(`/dashboard/invoicing/${data.id}/edit`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBulkBusy(false); }
  };

  const custName = (j) => {
    const c = customers.find((x) => x.id === j.client_user_id);
    return c?.business_name || c?.name || "—";
  };
  const driverName = (j) => {
    const d = drivers.find((x) => x.id === j.assigned_driver_id);
    return d?.name || "—";
  };
  const fmtDate = (s) => (s ? formatDate(s) : "—");

  // Inline driver reassignment straight from the list row, without opening
  // Edit Job. Sends the full job object (same shape the detail view's "Save
  // Changes" sends) since PUT /jobs/{id} validates against the full JobIn model.
  const [assigningId, setAssigningId] = useState(null);
  const assignDriver = async (j, driverId) => {
    setAssigningId(j.id);
    try {
      await api.put(`/jobs/${j.id}`, { ...j, assigned_driver_id: driverId || null });
      toast.success("Driver updated");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setAssigningId(null);
    }
  };

  if (active) {
    return (
      <JobDetailView
        job={active}
        drivers={drivers}
        customers={customers}
        role={user.role}
        onClose={() => setActive(null)}
        onChanged={async ({ keepOpen = false } = {}) => {
          await load();
          if (!keepOpen) setActive(null);
        }}
      />
    );
  }

  return (
    <div data-testid={historyMode ? "job-history-page" : "job-status-page"}>
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h1 className="brand-title text-2xl md:text-3xl">{historyMode ? "Job History" : "Job Status"}</h1>
          <div className="flex items-center gap-2">
            {isAdmin && !selectMode && (
              <button
                onClick={() => setSelectMode(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors"
                data-testid="select-jobs-btn"
              >
                <Trash2 size={14} /> Select
              </button>
            )}
            {isAdmin && selectMode && (
              <>
                <button
                  onClick={billTogether}
                  disabled={!canBillTogether || bulkBusy}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                  style={{ background: "rgba(236,72,153,0.15)", color: "#ec4899" }}
                  title={
                    selectedJobs.length < 2
                      ? "Select at least 2 jobs"
                      : (!billClientId || billClientId === "_none"
                          ? "Selected jobs must all belong to one customer"
                          : "Merge these jobs' invoices into one consolidated invoice")
                  }
                  data-testid="bill-together-btn"
                >
                  <Layers size={14} /> Bill together ({selected.size})
                </button>
                <button
                  onClick={deleteSelected}
                  disabled={selected.size === 0 || bulkBusy}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
                  style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444" }}
                  data-testid="delete-selected-btn"
                >
                  <Trash2 size={14} /> {bulkBusy ? "Deleting…" : `Delete (${selected.size})`}
                </button>
                <button
                  onClick={exitSelect}
                  className="px-3 py-2 rounded-lg bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors"
                >
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors"
            >
              <ArrowUpDown size={14} /> {sortDir === "desc" ? "Newest First" : "Oldest First"}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              type="text"
              placeholder="Search client ref, carrier ref, driver, customer…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="df-input-rect !py-2 !text-sm !pl-9 w-full"
            />
          </div>
          {historyMode ? (
            <>
              <span className="text-white/80 font-semibold text-sm">From:</span>
              <DatePicker value={dateFrom} onChange={setDateFrom} max={dateTo || undefined} className="!py-2 !text-sm" data-testid="filter-date-from" />
              <span className="text-white/80 font-semibold text-sm">To:</span>
              <DatePicker value={dateTo} onChange={setDateTo} min={dateFrom || undefined} className="!py-2 !text-sm" data-testid="filter-date-to" />
              {(dateFrom || dateTo) && (
                <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-white/60 hover:text-white text-sm underline">Clear</button>
              )}
            </>
          ) : (
            <>
              <span className="text-white/80 font-semibold text-sm">Date:</span>
              <DatePicker value={date} onChange={setDate} className="!py-2 !text-sm" data-testid="filter-date" />
              {date && <button onClick={() => setDate("")} className="text-white/60 hover:text-white text-sm underline">Clear</button>}
            </>
          )}
          {isAdmin && (
            <div className="inline-flex rounded-lg border border-white/10 overflow-hidden ml-auto">
              {[["all", "All"], ["merged", "Merged"], ["unbilled", "Unbilled"]].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setBillFilter(k)}
                  className={`px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                    billFilter === k ? "bg-[#19e6c8]/20 text-[#19e6c8]" : "bg-white/[0.03] text-white/55 hover:bg-white/10 hover:text-white"
                  }`}
                  data-testid={`bill-filter-${k}`}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="table-scroll rounded-2xl border border-white/10 bg-white/[0.02]">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[#19e6c8] text-left">
              {isAdmin && selectMode && (
                <th className="py-4 pl-5 pr-2 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 accent-[#ef4444] cursor-pointer align-middle"
                    aria-label="Select all"
                    data-testid="select-all"
                  />
                </th>
              )}
              {[
                { label: "Booking ID" },
                { label: "Client" },
                { label: "Driver" },
                { label: "PU Date" },
                { label: "Del Date" },
                { label: "Status" },
                { label: "Pick-Up / Drop-Off" },
                { label: "Item Details" },
              ].map((h) => (
                <th key={h.label} className="py-4 px-5 font-semibold tracking-wide whitespace-nowrap">{h.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={isAdmin && selectMode ? 9 : 8} className="py-10 text-center text-white/50">No jobs.</td></tr>
            )}
            {filtered.map((j) => {
              const isChecked = selected.has(j.id);
              const statusMeta = STATUS_META[j.status] || STATUS_META.pending;
              // "Assigned"/"pending" only means something once a driver is actually on
              // the job — the backend sets this status at creation regardless of
              // assigned_driver_id, so show a dash rather than a misleading badge.
              const noStatusBadge = !j.assigned_driver_id && (j.status === "assigned" || j.status === "pending" || !j.status);
              const itemLine = (j.item_qty || j.item_type) ? `${j.item_qty || 0} x ${j.item_type || "item"}` : "";
              return (
                <tr
                  key={j.id}
                  className={`border-t border-white/5 hover:bg-white/5 cursor-pointer transition-colors ${isChecked ? "bg-red-500/5" : ""}`}
                  onClick={() => (selectMode ? toggleSelect(j.id) : setActive(j))}
                  data-testid={`job-row-${j.job_ref}`}
                >
                  {isAdmin && selectMode && (
                    <td className="py-4 pl-5 pr-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleSelect(j.id)}
                        className="w-4 h-4 accent-[#ef4444] cursor-pointer align-middle"
                        aria-label={`Select ${j.job_ref}`}
                        data-testid={`select-job-${j.job_ref}`}
                      />
                    </td>
                  )}
                  {/* Booking ID — client ref + DRIVN carrier ref, plus a merged badge
                      so the Merged filter still flags consolidated jobs at a glance. */}
                  <td className="py-4 px-5 whitespace-nowrap">
                    <div className="text-white font-semibold">{j.job_ref || "—"}</div>
                    {j.carrier_ref && (
                      <div className="text-xs font-mono mt-0.5" style={{ color: "#a78bfa" }}>{j.carrier_ref}</div>
                    )}
                    {j.invoice_ref?.merged && (
                      <div className="mt-1">
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-pink-500/15 text-pink-300">merged · {j.invoice_ref.job_count}</span>
                      </div>
                    )}
                  </td>
                  <td className="py-4 px-5 text-white/80 max-w-[180px]">
                    <span className="truncate block" title={custName(j)}>{custName(j)}</span>
                  </td>
                  <td className="py-4 px-5 text-white/75 max-w-[150px]">
                    {canOperate ? (
                      <Select
                        value={j.assigned_driver_id ? String(j.assigned_driver_id) : "unassigned"}
                        onValueChange={(val) => assignDriver(j, val === "unassigned" ? "" : val)}
                        disabled={assigningId === j.id}
                      >
                        <SelectTrigger
                          onClick={(e) => e.stopPropagation()}
                          disabled={assigningId === j.id}
                          className="!h-auto w-full max-w-[150px] rounded-xl border-white/20 bg-white/5 backdrop-blur-md px-3 py-1.5 text-xs text-white/90 disabled:opacity-50"
                          data-testid={`assign-driver-row-${j.job_ref}`}
                        >
                          <SelectValue placeholder="Unassigned">
                            <span className="truncate block max-w-[100px]" title={driverName(j)}>
                              {j.assigned_driver_id ? driverName(j) : "Unassigned"}
                            </span>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent className="rounded-xl border border-white/10 bg-[#15151f]/95 text-white backdrop-blur-xl">
                          <SelectItem value="unassigned" className="text-xs focus:bg-white/10 focus:text-white">Unassigned</SelectItem>
                          {drivers.map((d) => (
                            <SelectItem key={d.id} value={String(d.id)} className="text-xs focus:bg-white/10 focus:text-white">
                              {d.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="truncate block" title={driverName(j)}>{driverName(j)}</span>
                    )}
                  </td>
                  <td className="py-4 px-5 text-white/70 whitespace-nowrap text-xs font-mono">{fmtDate(j.job_date)}</td>
                  <td className="py-4 px-5 text-white/70 whitespace-nowrap text-xs font-mono">{fmtDate(j.pod_completed_at || j.delivery_arrived_at)}</td>
                  <td className="py-4 px-5">
                    {noStatusBadge ? (
                      <span className="text-white/40">—</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
                        style={{ background: statusMeta.color + "22", color: statusMeta.color }}>
                        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: statusMeta.color }} /> {statusMeta.label}
                      </span>
                    )}
                  </td>
                  <td className="py-4 px-5 text-white/75 max-w-[220px]">
                    <div className="truncate" title={j.pickup_address}>PU: {j.pickup_address || "—"}</div>
                    <div className="truncate mt-0.5" title={j.dropoff_address}>DO: {j.dropoff_address || "—"}</div>
                  </td>
                  <td className="py-4 px-5 text-white/75 whitespace-nowrap text-xs">
                    <div>{itemLine || "—"}</div>
                    {(j.volume_cbm || j.weight_kgs) && (
                      <div className="text-white/45 mt-0.5">{j.volume_cbm || 0} cbm · {j.weight_kgs || 0} kgs</div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function JobDetailView({ job, onClose, onChanged, role, drivers, customers }) {
  const navigate = useNavigate();
  const [data, setData] = useState({ ...job });
  const [busy, setBusy] = useState(false);
  // "Other" when the stored freight type isn't one of the presets (and is set).
  const [freightSel, setFreightSel] = useState(() =>
    job.freight_type && !FREIGHT_TYPES.includes(job.freight_type) ? FREIGHT_OTHER : (job.freight_type || "LCL")
  );
  // Multi-select item rows — parsed from the job's stored "N x Type, N x Type" string.
  const [itemRows, setItemRows] = useState(() => parseItemRows(job.item_type, job.item_qty));
  // Multi-select toll presets — best-effort reverse-mapped from the job's stored flat total.
  const [tollSelected, setTollSelected] = useState(() => detectTollSelection(job.toll).selected);
  const [tollOther, setTollOther] = useState(() => detectTollSelection(job.toll).otherAmount);
  const [estimateInput, setEstimateInput] = useState(job.quote_amount != null ? String(job.quote_amount) : "");
  // ETA date defaults to the job's own delivery date so admin/driver usually
  // only need to pick the window, not re-pick a date each time.
  const [etaDate, setEtaDate] = useState(job.eta_date || job.job_date || "");
  const [etaWindow, setEtaWindow] = useState(job.eta_window || "");
  const [pushingEst, setPushingEst] = useState(false);
  const [pushingEta, setPushingEta] = useState(false);
  const [adminRemarks, setAdminRemarks] = useState(job.admin_remarks || "");
  const [savingRemarks, setSavingRemarks] = useState(false);
  const [extLink, setExtLink] = useState(job.external_token ? `${window.location.origin}/ext/${job.external_token}` : null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [eDocFiles, setEDocFiles] = useState(job.e_doc_files || []);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [sendingCnPod, setSendingCnPod] = useState(false);
  const [chatUnread, setChatUnread] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]); // address book, for stop search
  const lastSeenIdRef = useRef(null);
  const { user } = useAuth();

  useEffect(() => {
    if (role !== "admin") return;
    api.get("/addresses").then(({ data }) => setSavedAddresses(data)).catch(() => {});
  }, [role]);

  useEffect(() => {
    api.get(`/jobs/${job.id}/chat`).then(({ data }) => {
      if (!data.length) return;
      const latest = data[data.length - 1];
      lastSeenIdRef.current = latest.id;
      const stored = localStorage.getItem(`chat_lastseen_${job.id}`);
      if (stored && latest.id !== stored && latest.sender_id !== user?.id) {
        setChatUnread(true);
      }
    }).catch(() => {});
  }, [job.id, user?.id]);

  useEffect(() => {
    if (showChat) return;
    const t = setInterval(() => {
      api.get(`/jobs/${job.id}/chat`).then(({ data }) => {
        if (!data.length) return;
        const latest = data[data.length - 1];
        if (lastSeenIdRef.current !== null && latest.id !== lastSeenIdRef.current && latest.sender_id !== user?.id) {
          setChatUnread(true);
        }
        lastSeenIdRef.current = latest.id;
      }).catch(() => {});
    }, 25000);
    return () => clearInterval(t);
  }, [showChat, job.id, user?.id]);

  const openChat = () => {
    setShowChat(true);
    setChatUnread(false);
    if (lastSeenIdRef.current) {
      localStorage.setItem(`chat_lastseen_${job.id}`, lastSeenIdRef.current);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...data,
        buy_rate: Number(data.buy_rate) || 0,
        base_rate: Number(data.base_rate) || 0,
        fuel_levy_pct: Number(data.fuel_levy_pct) || 0,
        toll: Number(data.toll) || 0,
        extra_line_items: (data.extra_line_items || [])
          .filter((it) => (it.description || "").trim())
          .map((it) => ({ id: it.id, description: it.description.trim(), amount: Number(it.amount) || 0 })),
      };
      await api.put(`/jobs/${job.id}`, payload);
      toast.success("Job updated");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.patch(`/jobs/${job.id}/status`, { status });
      toast.success(`Status: ${status}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this job?")) return;
    setBusy(true);
    try {
      await api.delete(`/jobs/${job.id}`);
      toast.success("Job deleted");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  // Custom/extra invoice line items (e.g. "Shrink wrapping charges -
  // Interport") added on the job card — kept as an array on the job so they
  // auto-map onto the invoice (see _job_line_items() in the backend).
  const addExtraLineItem = () => {
    setData((d) => ({
      ...d,
      extra_line_items: [...(d.extra_line_items || []), { id: crypto.randomUUID?.() || String(Math.random()), description: "", amount: 0 }],
    }));
  };
  const updateExtraLineItem = (idx, field, val) => {
    setData((d) => ({
      ...d,
      extra_line_items: (d.extra_line_items || []).map((it, i) => (i === idx ? { ...it, [field]: val } : it)),
    }));
  };
  const removeExtraLineItem = (idx) => {
    setData((d) => ({
      ...d,
      extra_line_items: (d.extra_line_items || []).filter((_, i) => i !== idx),
    }));
  };

  // Allocator is a restricted staff role — full operational access to a job
  // (addresses, items, driver assignment, status, stops) but never pricing/
  // billing, which stays gated to `role === "admin"` individually below.
  const editable = role === "admin" || role === "allocator";
  // New stops default to the type the declared shipment shape implies, rather
  // than always "intermediate" — admin can still change it per stop.
  const defaultStopType = () => {
    if (data.shipment_mode === "multi_pickup") return "pickup";
    if (data.shipment_mode === "multi_dropoff") return "dropoff";
    if (data.shipment_mode === "multi_both") return "pickup";
    return "intermediate";
  };

  // Toll total is the sum of every selected preset + the custom "other"
  // amount — keeps data.toll (used by the live pricing total + Save payload)
  // in sync whenever the multi-select checkboxes change.
  useEffect(() => {
    setData((d) => ({ ...d, toll: tollTotal(tollSelected, tollOther) }));
  }, [tollSelected, tollOther]);

  // Item rows are edited as an array; keep data.item_type/item_qty (the
  // fields actually sent to the backend) derived from them.
  const updateItemRows = (rows) => {
    setItemRows(rows);
    setData((d) => ({ ...d, ...deriveItemFields(rows) }));
  };

  const pushEstimate = async (e) => {
    e?.preventDefault?.();
    const amount = parseFloat(estimateInput);
    if (isNaN(amount) || amount < 0) { toast.error("Enter a valid amount"); return; }
    setPushingEst(true);
    try {
      await api.patch(`/jobs/${job.id}/push-estimate`, { quote_amount: amount });
      toast.success(`Estimate $${amount.toFixed(2)} pushed to client`);
      // Refresh the list in the background but stay on this Job Card — a full
      // onChanged() bounces back to the list and drops any pricing edits in
      // `data` that haven't been "Save"d yet.
      onChanged({ keepOpen: true });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPushingEst(false); }
  };

  const pushEta = async (e) => {
    e?.preventDefault?.();
    if (!etaDate || !etaWindow) { toast.error("Pick a date and a delivery window"); return; }
    setPushingEta(true);
    try {
      await api.patch(`/jobs/${job.id}/push-eta`, { eta_date: etaDate, eta_window: etaWindow });
      toast.success("ETA pushed to client");
      // Stay on the Job Card — see note in pushEstimate above.
      onChanged({ keepOpen: true });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setPushingEta(false); }
  };

  const saveAdminRemarks = async (e) => {
    e?.preventDefault?.();
    setSavingRemarks(true);
    try {
      await api.patch(`/jobs/${job.id}/remarks`, { admin_remarks: adminRemarks });
      toast.success("Admin remarks saved");
      onChanged({ keepOpen: true });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingRemarks(false); }
  };

  const generateExtLink = async () => {
    setGeneratingLink(true);
    try {
      const { data: res } = await api.post(`/jobs/${job.id}/generate-driver-link`);
      const url = `${window.location.origin}/ext/${res.token}`;
      setExtLink(url);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Driver link generated & copied to clipboard");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setGeneratingLink(false); }
  };

  const uploadEDocs = async (files) => {
    if (!files.length) return;
    setUploadingDocs(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const { data: res } = await api.post(`/jobs/${job.id}/e-docs`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEDocFiles(res.e_doc_files || []);
      toast.success(`${files.length} file(s) uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploadingDocs(false); }
  };

  const deleteEDoc = async (path) => {
    try {
      await api.delete(`/jobs/${job.id}/e-docs`, { params: { path } });
      setEDocFiles((f) => f.filter((d) => d.path !== path));
      toast.success("File removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const downloadEDoc = async (path, name) => {
    try {
      const token = localStorage.getItem("df_token");
      const res = await fetch(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name || path.split("/").pop();
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
  };

  const printEDoc = async (path) => {
    // Open the tab synchronously, inside the click handler — opening it only
    // after the await below breaks the user-gesture chain and mobile browsers
    // (iOS Safari in particular) silently strand it on about:blank instead of
    // navigating to the blob once it's ready.
    const w = window.open("", "_blank", "noopener,noreferrer");
    try {
      const token = localStorage.getItem("df_token");
      const res = await fetch(`${API}/files/${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      if (!w) { toast.error("Please allow pop-ups to print documents"); return; }
      w.location.href = url;
      w.addEventListener("load", () => w.print());
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      if (w) w.close();
      toast.error(e.message);
    }
  };

  const waitCharge = calcWaitCharge(data.checkin_time, data.checkout_time);
  const dropoffWaitCharge = calcWaitCharge(data.dropoff_checkin_time, data.dropoff_checkout_time);

  // Live customer total (incl. GST) as admin edits pricing — mirrors
  // _job_sell_ex()/_job_sell_inc() in backend/server.py so the number shown
  // here always matches what actually gets billed/pushed as the estimate.
  // Express and Standard bill identically — Sell Rate is entered manually.
  const isExpress = data.service_type === "express";
  const effBase = Number(data.base_rate) || 0;
  const fuelAmt = +(effBase * (Number(data.fuel_levy_pct) || 0) / 100).toFixed(2);
  const tollAmt = Number(data.toll) || 0;
  const accTotal = ACCESSORIAL_CHARGES.reduce((s, [k, , price]) => s + (data[k] === "Yes" ? price : 0), 0);
  const sellEx = +(effBase + fuelAmt + tollAmt + accTotal).toFixed(2);
  const sellIncGst = +(sellEx * 1.1).toFixed(2);

  return (
    <div className="fade-up" data-testid="job-detail-page">
      {/* Back button + title */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.06] hover:bg-white/10 text-white/70 hover:text-white text-sm font-semibold transition-colors shrink-0"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div>
          <h3 className="brand-title text-2xl truncate">Job: {data.job_ref || "—"}</h3>
          {data.carrier_ref && (
            <p className="text-xs font-mono mt-0.5" style={{ color: "#a78bfa" }}>{data.carrier_ref}</p>
          )}
        </div>
      </div>

      {/* Main content card */}
      <div className="bg-[#15151f] border border-white/10 rounded-2xl p-4 sm:p-7" data-testid="job-detail-modal">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Lbl label="Job Date"><DatePicker disabled={!editable} value={data.job_date} onChange={(v) => set("job_date", v)} /></Lbl>
          <Lbl label="Client Reference / Booking ID"><input disabled={!editable} value={data.job_ref} onChange={(e) => set("job_ref", e.target.value)} className="df-input-rect" placeholder="Client's own reference number" /></Lbl>
          {data.carrier_ref && (
            <Lbl label="Carrier Reference">
              <input disabled value={data.carrier_ref} className="df-input-rect opacity-70 font-mono" style={{ color: "#a78bfa" }} />
            </Lbl>
          )}
          <Lbl label="Pick-Up Address">
            <AddressAutocomplete
              disabled={!editable}
              value={data.pickup_address || ""}
              onChange={(v) => set("pickup_address", v)}
              onResolved={(p) => set("pickup_address", p.formatted || p.line1)}
              className="df-input-rect"
            />
            {editable ? (
              <input
                value={data.pickup_operating_hours || ""}
                onChange={(e) => set("pickup_operating_hours", e.target.value)}
                placeholder="Operating hours (optional) — e.g. Mon–Fri 7am–4pm"
                className="df-input-rect mt-2 !text-xs"
              />
            ) : data.pickup_operating_hours ? (
              <p className="text-xs text-[#facc15] mt-1.5">Operating hours: {data.pickup_operating_hours}</p>
            ) : null}
          </Lbl>
          <Lbl label="Drop-Off Address">
            <AddressAutocomplete
              disabled={!editable}
              value={data.dropoff_address || ""}
              onChange={(v) => set("dropoff_address", v)}
              onResolved={(p) => set("dropoff_address", p.formatted || p.line1)}
              className="df-input-rect"
            />
            {editable ? (
              <input
                value={data.dropoff_operating_hours || ""}
                onChange={(e) => set("dropoff_operating_hours", e.target.value)}
                placeholder="Operating hours (optional) — e.g. Mon–Fri 7am–4pm"
                className="df-input-rect mt-2 !text-xs"
              />
            ) : data.dropoff_operating_hours ? (
              <p className="text-xs text-[#facc15] mt-1.5">Operating hours: {data.dropoff_operating_hours}</p>
            ) : null}
          </Lbl>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Pick-Up Contact Name">
              <input disabled={!editable} value={data.pickup_contact_name || ""} onChange={(e) => set("pickup_contact_name", e.target.value)} className="df-input-rect" placeholder="Sender contact" />
            </Lbl>
            <Lbl label="Pick-Up Contact Phone">
              <input disabled={!editable} value={data.pickup_contact_phone || ""} onChange={(e) => set("pickup_contact_phone", e.target.value)} className="df-input-rect" />
            </Lbl>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Lbl label="Drop-Off Contact Name">
              <input disabled={!editable} value={data.dropoff_contact_name || ""} onChange={(e) => set("dropoff_contact_name", e.target.value)} className="df-input-rect" placeholder="Receiver contact" />
            </Lbl>
            <Lbl label="Drop-Off Contact Phone">
              <input disabled={!editable} value={data.dropoff_contact_phone || ""} onChange={(e) => set("dropoff_contact_phone", e.target.value)} className="df-input-rect" />
            </Lbl>
          </div>

          <Lbl label="Shipment Type">
            <select disabled={!editable} value={data.shipment_mode || "single"} onChange={(e) => set("shipment_mode", e.target.value)} className="df-input-rect">
              {SHIPMENT_MODES.map((m) => <option key={m.value} value={m.value} className="bg-[#15151f]">{m.label}</option>)}
            </select>
          </Lbl>

          <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["inbound_connect","Inbound Connect"],["tail_lift","Tail Lift"],["hand_unload","Hand Unload"],["dangerous_goods","Dangerous Goods"]].map(([k,l]) => (
              <Lbl key={k} label={l}>
                <select disabled={!editable} value={data[k]} onChange={(e) => set(k, e.target.value)} className="df-input-rect">
                  {["Yes","No","N/A"].map((o) => <option key={o} className="bg-[#15151f]">{o}</option>)}
                </select>
              </Lbl>
            ))}
          </div>

          <Lbl label="Service Type">
            <select disabled={!editable} value={data.service_type || "standard"} onChange={(e) => set("service_type", e.target.value)} className="df-input-rect" data-testid="service-type">
              {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value} className="bg-[#15151f]">{s.label}</option>)}
            </select>
          </Lbl>

          <Lbl label="Freight Type">
            <select
              disabled={!editable}
              value={freightSel}
              onChange={(e) => {
                setFreightSel(e.target.value);
                set("freight_type", e.target.value === FREIGHT_OTHER ? "" : e.target.value);
              }}
              className="df-input-rect"
            >
              {[...FREIGHT_TYPES, FREIGHT_OTHER].map((o) => <option key={o} value={o} className="bg-[#15151f]">{o}</option>)}
            </select>
            {freightSel === FREIGHT_OTHER && (
              <input
                disabled={!editable}
                value={data.freight_type || ""}
                onChange={(e) => set("freight_type", e.target.value)}
                placeholder="Specify freight type"
                className="df-input-rect mt-2"
              />
            )}
          </Lbl>
          <Lbl label="Item Type + Qty">
            <ItemMultiSelect rows={itemRows} onRowsChange={updateItemRows} disabled={!editable} />
          </Lbl>
          <Lbl label="Volume (cbm)"><input disabled={!editable} type="number" step="0.01" value={data.volume_cbm} onChange={(e) => set("volume_cbm", Number(e.target.value))} className="df-input-rect" /></Lbl>
          <Lbl label="Weight (kgs)"><input disabled={!editable} type="number" value={data.weight_kgs} onChange={(e) => set("weight_kgs", Number(e.target.value))} className="df-input-rect" /></Lbl>

          <Lbl label="Dimensions"><textarea rows="3" disabled={!editable} value={data.dimensions} onChange={(e) => set("dimensions", e.target.value)} className="df-input-rect resize-none" /></Lbl>
          <Lbl label="Notes"><textarea rows="3" disabled={!editable} value={data.notes} onChange={(e) => set("notes", e.target.value)} className="df-input-rect resize-none" /></Lbl>

          {editable && (
            <Lbl label="Assign Driver">
              <select value={data.assigned_driver_id || ""} onChange={(e) => set("assigned_driver_id", e.target.value || null)} className="df-input-rect">
                <option value="" className="bg-[#15151f]">Unassigned</option>
                {drivers.map((d) => <option key={d.id} value={d.id} className="bg-[#15151f]">{d.name} ({d.email})</option>)}
              </select>
            </Lbl>
          )}

          {role === "admin" && (
            <Lbl label="Client">
              <select value={data.client_user_id || ""} onChange={(e) => set("client_user_id", e.target.value || null)} className="df-input-rect">
                <option value="" className="bg-[#15151f]">— No client —</option>
                {customers.map((c) => <option key={c.id} value={c.id} className="bg-[#15151f]">{c.business_name || c.name} ({c.email})</option>)}
              </select>
            </Lbl>
          )}

          {role === "admin" && (
            <div className="md:col-span-2 rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <p className="text-xs text-[#19e6c8] font-semibold uppercase tracking-widest mb-3">Pricing (AUD)</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Lbl label="Driver Rate (cost)">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                    <input type="number" step="0.01" min="0" value={blankIfZero(data.buy_rate)} onChange={(e) => set("buy_rate", e.target.value)} className="df-input-rect !pl-7" placeholder="0.00" />
                  </div>
                </Lbl>
                <Lbl label="Customer Rate">
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                    <input type="number" step="0.01" min="0" value={blankIfZero(data.base_rate)} onChange={(e) => set("base_rate", e.target.value)} className="df-input-rect !pl-7" placeholder="0.00" />
                  </div>
                </Lbl>
                <Lbl label="Fuel Levy (% of base)">
                  <div className="relative">
                    <input type="number" step="0.1" min="0" max="100" value={blankIfZero(data.fuel_levy_pct)} onChange={(e) => set("fuel_levy_pct", e.target.value)} className="df-input-rect !pr-8" placeholder="0" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">%</span>
                  </div>
                </Lbl>
                <Lbl label="Toll">
                  <TollMultiSelect
                    selected={tollSelected}
                    otherAmount={tollOther}
                    onChangeSelected={setTollSelected}
                    onChangeOtherAmount={setTollOther}
                  />
                </Lbl>
              </div>
              <div className="mt-4 pt-3 border-t border-white/10 flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-white/50">
                  Customer Total (incl. GST){isExpress ? " — Express" : ""}
                </span>
                <span className="text-lg font-bold font-mono text-[#19e6c8]" data-testid="pricing-total-inc-gst">
                  ${sellIncGst.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>

              <div className="mt-4 pt-3 border-t border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-white/50 uppercase tracking-widest">Custom Line Items</span>
                  <button
                    type="button"
                    onClick={addExtraLineItem}
                    className="text-xs font-semibold px-2 py-1 rounded-lg flex items-center gap-1"
                    style={{ background: "rgba(25,230,200,0.15)", color: "#19e6c8" }}
                    data-testid="add-extra-line-item"
                  >
                    <Plus size={12} /> Add item
                  </button>
                </div>
                {(data.extra_line_items || []).length === 0 ? (
                  <p className="text-[11px] text-white/30">
                    E.g. “Shrink wrapping charges - Interport”. Items added here flow straight onto the invoice as their own line.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {data.extra_line_items.map((item, idx) => (
                      <div key={item.id || idx} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updateExtraLineItem(idx, "description", e.target.value)}
                          placeholder="Description (e.g. Shrink wrapping charges - Interport)"
                          className="df-input-rect flex-1"
                          data-testid={`extra-line-desc-${idx}`}
                        />
                        <div className="relative w-28 shrink-0">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                          <input
                            type="number" step="0.01"
                            value={blankIfZero(item.amount)}
                            onChange={(e) => updateExtraLineItem(idx, "amount", e.target.value)}
                            className="df-input-rect !pl-7"
                            placeholder="0.00"
                            data-testid={`extra-line-amount-${idx}`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeExtraLineItem(idx)}
                          className="p-2 rounded-lg text-red-400 hover:bg-red-500/10 shrink-0"
                          title="Remove"
                          data-testid={`extra-line-remove-${idx}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {editable && (
            <div className={`md:col-span-2 rounded-xl border border-white/10 bg-white/[0.02] p-4 grid grid-cols-1 gap-4 ${role === "admin" ? "md:grid-cols-2" : ""}`}>
              {/* Pushing a $ estimate to the client is billing-adjacent — admin only. */}
              {role === "admin" && (
                <div>
                  <p className="text-xs text-[#19e6c8] font-semibold uppercase tracking-widest mb-2">Push Estimate to Client (incl. GST)</p>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm">$</span>
                      <input
                        type="number" step="0.01" min="0"
                        value={estimateInput}
                        onChange={(e) => setEstimateInput(e.target.value)}
                        className="df-input-rect !pl-7"
                        placeholder="0.00"
                        data-testid="estimate-input"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setEstimateInput(String(sellIncGst))}
                      disabled={sellIncGst <= 0}
                      className="px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap disabled:opacity-40"
                      style={{ background: "rgba(255,255,255,0.06)", color: "rgba(255,255,255,0.7)" }}
                      title="Fill with the computed pricing total"
                    >
                      Use ${sellIncGst.toFixed(2)}
                    </button>
                    <button
                      type="button"
                      onClick={pushEstimate}
                      disabled={pushingEst}
                      className="px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
                      style={{ background: "rgba(25,230,200,0.15)", color: "#19e6c8" }}
                      data-testid="push-estimate-btn"
                    >
                      {pushingEst ? "..." : "Estimate"}
                    </button>
                  </div>
                </div>
              )}
              {/* Push ETA is pure scheduling, not billing — available to allocators too. */}
              {editable && (
                <div>
                  <p className="text-xs text-[#a78bfa] font-semibold uppercase tracking-widest mb-2">Push ETA to Client</p>
                  <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                    <DatePicker
                      value={etaDate}
                      onChange={setEtaDate}
                      className="flex-1 min-w-[9rem]"
                      data-testid="eta-date-input"
                    />
                    <select
                      value={etaWindow}
                      onChange={(e) => setEtaWindow(e.target.value)}
                      className="df-input-rect flex-1 min-w-[9rem]"
                      data-testid="eta-window-input"
                    >
                      <option value="" className="bg-[#15151f]">Select window</option>
                      {ETA_WINDOWS.map((w) => (
                        <option key={w.value} value={w.value} className="bg-[#15151f]">{w.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={pushEta}
                      disabled={pushingEta}
                      className="px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap"
                      style={{ background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}
                      data-testid="push-eta-btn"
                    >
                      {pushingEta ? "..." : "ETA"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {role === "admin" && (
            <div className="md:col-span-2 rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <p className="text-xs text-[#facc15] font-semibold uppercase tracking-widest mb-3">Pickup Wait Time</p>
              <p className="text-[11px] text-white/40 mb-3">Wait time runs from when the driver taps “Arrived at Pickup” to “Complete Pickup”. Auto-filled by the driver app; editable below.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Lbl label="Arrived at Pickup">
                  <DateTimePicker
                    value={toDatetimeLocal(data.checkin_time)}
                    onChange={(v) => set("checkin_time", v ? new Date(v).toISOString() : null)}
                    data-testid="checkin-input"
                  />
                </Lbl>
                <Lbl label="Pickup Completed">
                  <DateTimePicker
                    value={toDatetimeLocal(data.checkout_time)}
                    onChange={(v) => set("checkout_time", v ? new Date(v).toISOString() : null)}
                    data-testid="checkout-input"
                  />
                </Lbl>
              </div>
              {waitCharge && (
                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                  <span className="px-3 py-1.5 rounded-lg" style={{ background: "rgba(250,204,21,0.08)", color: "#facc15" }}>
                    Total: {waitCharge.total} min
                  </span>
                  <span className="px-3 py-1.5 rounded-lg" style={{ background: "rgba(250,204,21,0.08)", color: "#facc15" }}>
                    Billable: {waitCharge.billable} min (after 30 min free)
                  </span>
                  <span className="px-3 py-1.5 rounded-lg font-semibold" style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e" }}>
                    Wait Charge: ${waitCharge.charge}
                  </span>
                </div>
              )}
            </div>
          )}

          {role === "admin" && (
            <div className="md:col-span-2 rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <p className="text-xs text-[#facc15] font-semibold uppercase tracking-widest mb-3">Drop-off Wait Time</p>
              <p className="text-[11px] text-white/40 mb-3">Wait time runs from when the driver taps “Arrived at Delivery” to “Complete Delivery”. Auto-filled by the driver app; editable below.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Lbl label="Arrived at Drop-off">
                  <DateTimePicker
                    value={toDatetimeLocal(data.dropoff_checkin_time)}
                    onChange={(v) => set("dropoff_checkin_time", v ? new Date(v).toISOString() : null)}
                    data-testid="dropoff-checkin-input"
                  />
                </Lbl>
                <Lbl label="Drop-off Completed">
                  <DateTimePicker
                    value={toDatetimeLocal(data.dropoff_checkout_time)}
                    onChange={(v) => set("dropoff_checkout_time", v ? new Date(v).toISOString() : null)}
                    data-testid="dropoff-checkout-input"
                  />
                </Lbl>
              </div>
              {dropoffWaitCharge && (
                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                  <span className="px-3 py-1.5 rounded-lg" style={{ background: "rgba(250,204,21,0.08)", color: "#facc15" }}>
                    Total: {dropoffWaitCharge.total} min
                  </span>
                  <span className="px-3 py-1.5 rounded-lg" style={{ background: "rgba(250,204,21,0.08)", color: "#facc15" }}>
                    Billable: {dropoffWaitCharge.billable} min (after 30 min free)
                  </span>
                  <span className="px-3 py-1.5 rounded-lg font-semibold" style={{ background: "rgba(34,197,94,0.1)", color: "#22c55e" }}>
                    Wait Charge: ${dropoffWaitCharge.charge}
                  </span>
                </div>
              )}
            </div>
          )}

          <Lbl label="Status">
            <div className="flex flex-wrap gap-2">
              {["needs_approval", "pending", "onboard", "done", "futile"].map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={role === "client"}
                  onClick={() => setStatus(s)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${data.status === s ? "ring-2 ring-white/40" : ""}`}
                  style={{ background: STATUS_META[s].color + "33", color: STATUS_META[s].color }}
                  data-testid={`status-btn-${s}`}
                >
                  {STATUS_META[s].label}
                </button>
              ))}
            </div>
          </Lbl>
        </div>

        {/* Live invoice readout — invoicing now lives in the Invoicing tab.
            Reflects the authoritative invoice (incl. merges/edits); for a merged
            invoice this shows THIS job's share of the consolidated total. */}
        {/* Belt-and-suspenders: the backend never attaches invoice_ref for an
            allocator's job list, but guard here too since this is financial data. */}
        {role !== "driver" && role !== "allocator" && job.invoice_ref && (
          <div
            className="mt-6 rounded-xl border border-white/10 bg-white/[0.03] p-4 cursor-pointer hover:bg-white/[0.05] hover:border-white/20 transition-colors"
            data-testid="job-invoice-ref"
            onClick={() => navigate(`/dashboard/invoicing?inv=${encodeURIComponent(job.invoice_ref.invoice_number)}`)}
          >
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2 text-sm">
                <Receipt size={15} className="text-pink-400" />
                <span className="text-white/55">Invoice</span>
                <span className="font-mono font-semibold text-white">{job.invoice_ref.invoice_number}</span>
                {job.invoice_ref.merged && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-300">
                    merged · {job.invoice_ref.job_count} jobs
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm">
                <span className="font-mono text-emerald-300">
                  ${Number(job.invoice_ref.amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 capitalize">
                  {(job.invoice_ref.invoice_status || "").replace(/_/g, " ")}
                </span>
              </div>
            </div>
            <p className="text-[11px] text-white/40 mt-2">
              <span className="text-[#19e6c8] hover:underline">Open in Invoicing tab</span>
              {job.invoice_ref.merged ? " — this is this job's share of a merged invoice." : "."}
            </p>
          </div>
        )}

        <div className="flex flex-wrap justify-between gap-3 mt-8">
          {role === "admin" ? (
            <button onClick={remove} disabled={busy} className="px-5 py-2.5 rounded-lg text-white font-semibold text-sm" style={{ background: "#dc2626" }} data-testid="delete-job-btn">Delete Job</button>
          ) : <span />}
          <div className="flex flex-wrap gap-2 justify-end flex-1">
            <DocBtn label="Consignment Note" jobId={job.id} jobRef={job.job_ref} type="cn" testid="dl-cn" />
            {editable && (
              <button onClick={() => setSendingCnPod(true)} className="px-4 py-2.5 rounded-lg text-white font-semibold flex items-center gap-2 bg-white/10 hover:bg-white/15" data-testid="send-cn-btn">
                <Mail size={14} /> Send CN and POD
              </button>
            )}
            <button onClick={onClose} className="px-5 py-2.5 rounded-lg text-white font-semibold" style={{ background: "#3b82f6" }}>Back</button>
            {editable && (
              <button onClick={save} disabled={busy} className="px-5 py-2.5 rounded-lg text-white font-semibold" style={{ background: "#22c55e" }} data-testid="save-job-btn">Save Changes</button>
            )}
          </div>
        </div>

        {sendingCnPod && (
          <SendCnPodModal
            job={data}
            defaultTo={[
              drivers.find((d) => d.id === data.assigned_driver_id)?.email,
              (() => { const c = customers.find((c) => c.id === data.client_user_id); return c && (c.primary_email || c.email); })(),
            ].filter(Boolean).join(", ")}
            defaultCc={(customers.find((c) => c.id === data.client_user_id)?.secondary_emails || []).join(", ")}
            podCount={data.pod_image_urls?.length || 0}
            onClose={() => setSendingCnPod(false)}
            onSent={() => setSendingCnPod(false)}
          />
        )}

        {/* Driver Notes — admin-only, visible when driver has submitted notes */}
        {role === "admin" && job.driver_notes && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center gap-2 mb-3">
              <StickyNote size={15} className="text-blue-400" />
              <h4 className="brand-title text-base text-blue-400">Driver Completion Notes</h4>
            </div>
            <div className="px-4 py-3 rounded-xl" style={{ background: "rgba(59,130,246,0.07)", border: "1px solid rgba(59,130,246,0.2)" }}>
              <p className="text-sm text-white/85 leading-relaxed">{job.driver_notes}</p>
              {job.driver_notes_updated_at && (
                <p className="text-xs text-white/35 mt-2">
                  Submitted: {formatDateTime(job.driver_notes_updated_at)}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Remarks section — shown when job is done */}
        {(data.status === "done" || job.status === "done") && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <h4 className="brand-title text-lg mb-4">Job Remarks</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-white/85 mb-1 font-semibold text-sm">Client Remarks</label>
                <textarea
                  rows="3"
                  readOnly
                  value={job.client_remarks || ""}
                  placeholder="No client remarks yet."
                  className="df-input-rect resize-none opacity-70"
                />
                {job.client_remarks_updated_at && (
                  <p className="text-xs text-white/40 mt-1">
                    Updated: {formatDateTime(job.client_remarks_updated_at)}
                  </p>
                )}
              </div>
              {role === "admin" && (
                <div>
                  <label className="block text-white/85 mb-1 font-semibold text-sm">Admin Remarks</label>
                  <textarea
                    rows="3"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                    placeholder="Add admin remarks..."
                    className="df-input-rect resize-none"
                    data-testid="admin-remarks-input"
                  />
                  {job.admin_remarks_updated_at && (
                    <p className="text-xs text-white/40 mt-1">
                      Updated: {formatDateTime(job.admin_remarks_updated_at)}
                    </p>
                  )}
                  <button
                    onClick={saveAdminRemarks}
                    disabled={savingRemarks}
                    className="mt-2 px-4 py-2 rounded-lg text-sm font-semibold"
                    style={{ background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}
                    data-testid="save-admin-remarks-btn"
                  >
                    {savingRemarks ? "Saving..." : "Save Admin Remarks"}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* POD / signatures section */}
        {(job.pod_image_urls?.length > 0 || job.signature_url || job.sender_signature_url || (role === "admin" && job.onboard_image_urls?.length > 0)) && (
          <div className="mt-8 pt-6 border-t border-white/10">
            <h4 className="brand-title text-lg mb-3">
              {job.pod_image_urls?.length > 0 || job.signature_url ? "Proof of Delivery" : "Pickup Confirmation"}
            </h4>
            <PodGallery
              imageUrls={job.pod_image_urls || []}
              pickupImageUrls={role === "admin" ? (job.onboard_image_urls || []) : []}
              senderSignatureUrl={job.sender_signature_url}
              signatureUrl={job.signature_url}
              podCompletedAt={job.pod_completed_at}
            />
          </div>
        )}

        {/* Legs / stops — editable for admins so a stop can be added after a job
            has already been accepted (e.g. the customer calls to add a stop). */}
        {editable ? (
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center justify-between mb-3">
              <h4 className="brand-title text-lg">Additional Pickups / Deliveries / Stops</h4>
              <button
                type="button"
                onClick={() => set("legs", [...(data.legs || []), { address: "", stop_type: defaultStopType(), notes: "", contact_name: "", contact_phone: "" }])}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/15 text-white"
                data-testid="add-stop-btn"
              >
                <Plus size={13} /> Add Stop
              </button>
            </div>
            {(data.legs || []).length === 0 && (
              <p className="text-xs text-white/30 py-2">Add each extra sender or receiver as its own stop — each gets its own signature and proof of delivery.</p>
            )}
            <div className="space-y-3">
              {(data.legs || []).map((leg, i) => (
                <div key={i} className="border border-white/10 rounded-xl p-4 bg-white/[0.02] space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#19e6c8] uppercase tracking-widest">Stop {i + 1}</span>
                    {leg.completed_at && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">Done</span>
                    )}
                    <button
                      type="button"
                      onClick={() => set("legs", (data.legs || []).filter((_, idx) => idx !== i))}
                      className="ml-auto text-red-400 hover:text-red-300"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <StopAddressField
                        value={leg.address}
                        onChange={(v) => set("legs", (data.legs || []).map((s, idx) => idx === i ? { ...s, address: v } : s))}
                        onResolved={(p) => set("legs", (data.legs || []).map((s, idx) => idx === i ? { ...s, address: p.formatted || p.line1 } : s))}
                        onPickSaved={(a) => set("legs", (data.legs || []).map((s, idx) => idx === i ? {
                          ...s,
                          contact_name: s.contact_name || a.contact_name || "",
                          contact_phone: s.contact_phone || a.contact_phone || "",
                        } : s))}
                        saved={savedAddresses}
                        placeholder="Stop address — search saved locations, search Google Maps, or type manually"
                        className="df-input-rect w-full"
                        testid={`stop-address-${i}`}
                      />
                    </div>
                    <select
                      value={leg.stop_type || "intermediate"}
                      onChange={(e) => set("legs", (data.legs || []).map((s, idx) => idx === i ? { ...s, stop_type: e.target.value } : s))}
                      className="df-input-rect"
                    >
                      <option value="pickup" className="bg-[#15151f]">Additional Pickup</option>
                      <option value="intermediate" className="bg-[#15151f]">Intermediate Stop</option>
                      <option value="dropoff" className="bg-[#15151f]">Delivery</option>
                    </select>
                    <input
                      value={leg.contact_name || ""}
                      onChange={(e) => set("legs", (data.legs || []).map((s, idx) => idx === i ? { ...s, contact_name: e.target.value } : s))}
                      placeholder="Contact name"
                      className="df-input-rect"
                    />
                    <input
                      value={leg.contact_phone || ""}
                      onChange={(e) => set("legs", (data.legs || []).map((s, idx) => idx === i ? { ...s, contact_phone: e.target.value } : s))}
                      placeholder="Contact phone"
                      className="df-input-rect"
                    />
                    <input
                      value={leg.notes || ""}
                      onChange={(e) => set("legs", (data.legs || []).map((s, idx) => idx === i ? { ...s, notes: e.target.value } : s))}
                      placeholder="Notes for this stop"
                      className="df-input-rect sm:col-span-2"
                    />
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-white/30 mt-2">Click “Save Changes” below to apply added or edited stops.</p>
          </div>
        ) : (
          job.legs?.length > 0 && (
            <div className="mt-6 pt-6 border-t border-white/10">
              <h4 className="brand-title text-lg mb-3">Intermediate Stops</h4>
              <div className="space-y-3">
                {job.legs.map((leg, i) => (
                  <div key={i} className="flex items-start gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10">
                    <span className="text-xs font-bold text-[#19e6c8] mt-0.5 w-16 shrink-0">Stop {i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-semibold">{leg.address}</p>
                      {leg.stop_type && <p className="text-xs text-white/40 capitalize">{leg.stop_type}</p>}
                      {leg.notes && <p className="text-xs text-white/50 italic mt-0.5">"{leg.notes}"</p>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${leg.completed_at ? "bg-green-500/20 text-green-400" : "bg-white/5 text-white/30"}`}>
                      {leg.completed_at ? "Done" : "Pending"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )
        )}

        {/* External driver link */}
        {editable && (
          <div className="mt-6 pt-6 border-t border-white/10">
            <div className="flex items-center justify-between mb-3">
              <h4 className="brand-title text-base">External Driver Link</h4>
              <button
                onClick={generateExtLink}
                disabled={generatingLink}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
                style={{ background: "rgba(167,139,250,0.15)", color: "#a78bfa" }}
                data-testid="generate-ext-link-btn"
              >
                <Link size={14} /> {generatingLink ? "Generating…" : (extLink ? "Regenerate Link" : "Generate Link")}
              </button>
            </div>
            {extLink && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10">
                <span className="text-sm text-white/70 truncate flex-1">{extLink}</span>
                <button
                  onClick={() => { navigator.clipboard.writeText(extLink); toast.success("Copied!"); }}
                  className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1"
                  style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}
                >
                  <Copy size={12} /> Copy
                </button>
              </div>
            )}
            <p className="text-xs text-white/30 mt-2">Share this link with an external driver to allow them to upload photos and signatures without logging in.</p>
          </div>
        )}

        {/* E-Documents */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <div className="flex items-center justify-between mb-3">
            <h4 className="brand-title text-base flex items-center gap-2">
              <FileText size={15} className="text-[#19e6c8]" /> E-Documents
            </h4>
            {editable && (
              <label className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer" style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}>
                <Upload size={13} /> {uploadingDocs ? "Uploading…" : "Upload Files"}
                <input
                  type="file"
                  multiple
                  hidden
                  disabled={uploadingDocs}
                  onChange={(e) => uploadEDocs(Array.from(e.target.files || []))}
                  data-testid="edoc-upload-input"
                />
              </label>
            )}
          </div>
          {eDocFiles.length === 0 && (
            <p className="text-xs text-white/30 italic">No e-documents uploaded yet.</p>
          )}
          <div className="space-y-2">
            {eDocFiles.map((doc, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/[0.03] border border-white/10">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white/80 truncate">{doc.name}</div>
                  <div className="text-[10px] text-white/40">{doc.doc_type || "Document"}{doc.uploaded_by === "client" ? " · from client" : ""}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => printEDoc(doc.path)} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1" style={{ background: "rgba(167,139,250,0.12)", color: "#a78bfa" }}>
                    <Printer size={11} /> Print
                  </button>
                  <button onClick={() => downloadEDoc(doc.path, doc.name)} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1" style={{ background: "rgba(25,230,200,0.1)", color: "#19e6c8" }}>
                    <Download size={11} /> Download
                  </button>
                  {editable && (
                    <button onClick={() => deleteEDoc(doc.path)} className="px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1" style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }} data-testid={`delete-edoc-${i}`}>
                      <Trash2 size={11} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Chat */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <button
            onClick={() => showChat ? setShowChat(false) : openChat()}
            className="flex items-center gap-2 text-sm font-semibold text-white/60 hover:text-white mb-3"
            data-testid="toggle-chat-btn"
          >
            <span className="relative inline-flex items-center">
              {showChat ? "▾" : "▸"} Driver ↔ Admin Chat
              {chatUnread && (
                <span className="ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white animate-pulse" style={{ background: "#ef4444" }}>NEW</span>
              )}
            </span>
          </button>
          {showChat && <ChatPanel jobId={job.id} jobRef={job.job_ref} />}
        </div>
      </div>
    </div>
  );
}

const Lbl = ({ label, children }) => (
  <div>
    <label className="block text-white/85 mb-2 font-semibold text-sm">{label}</label>
    {children}
  </div>
);

function DocBtn({ label, jobId, jobRef, type, testid }) {
  const [loading, setLoading] = useState(false);
  const onClick = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("df_token");
      const path = type === "cn" ? "consignment-note.pdf" : "invoice.pdf";
      const res = await fetch(`${API}/jobs/${jobId}/${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Download failed (${res.status}) ${txt.slice(0, 100)}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = type === "cn" ? `CN-${jobRef}.pdf` : `INV-${jobRef}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  const isCN = type === "cn";
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="px-4 py-2.5 rounded-lg font-semibold flex items-center gap-2 transition-opacity disabled:opacity-60"
      style={isCN
        ? { background: "rgba(25,230,200,0.1)", color: "#19e6c8" }
        : { background: "rgba(111,76,255,0.12)", color: "#a78bfa" }}
      data-testid={testid}
    >
      <Download size={14} /> {loading ? "..." : label}
    </button>
  );
}
